import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";
import { CircleSwitcherItem } from "@similarweb/ui-components";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { translate } from "app/filters/i18nFilter";
import { ELandingPagesTabs } from "app/common/keyword-research/landing-pages/landingPagesTypes";
import { getShareValue, getVolumeValue } from "app/pages/website-analysis/traffic-sources/search/common/utils";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import DurationServiceInstance from "app/services/DurationService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";

import {
    changeFilterItems,
    TABLE_COLUMN_CLICKS,
    TABLE_COLUMN_CLICKS_CHANGE,
    TABLE_COLUMN_SHARE,
    TABLE_COLUMN_TOP_URL,
    WIDGET_FOOTER_PAGES_LINK_TEXT,
    WIDGET_PAGES_TITLE_KEY,
    WIDGET_PAGES_TOOLTIP_KEY,
} from "./const";
import { SwitcherWrapper, TableWrapper } from "./StyledComponents";
import { getColumns } from "./table/ColumnConfig";
import { getNoDataObject } from "./table/EmptyState";
const API = "/api/WebsiteAnalysis/Overview/TopOrganicPages";

interface ITopOrganicPagesTableData {
    data: {
        changeState: string;
        clicksChange: number;
        clicksShare: number;
        totalClicks: number;
        keywordsCount: number;
        url: string;
    }[];
}

interface ChangeData {
    change: number;
    changeState: string;
}

interface ITransformedData {
    Url: string;
    Share: number;
    Clicks: number;
    ClicksChange: ChangeData;
    KeywordsCount: number;
}
function transformedData(data: ITopOrganicPagesTableData): ITransformedData[] {
    return data.data.map(({ clicksChange, clicksShare, totalClicks, changeState, url, keywordsCount, ...rest }) => ({
        ...rest,
        KeywordsCount: keywordsCount,
        Url: url,
        Share: getShareValue(clicksShare),
        Clicks: getVolumeValue(totalClicks),
        ClicksChange: {
            change: clicksChange,
            changeState: changeState,
        },
    }));
}

export const TopOrganicPages = () => {
    const { params } = useCurrentStateAndParams();
    const { duration, webSource, country, key, selectedPageTab, pageFilter, TopOrganicPagesFilter } = params;
    const changeIndex = changeFilterItems.findIndex((item) => item.id === TopOrganicPagesFilter);
    const [selectedChangeIndex, setSelectedChangeIndex] = useState(changeIndex > 0 ? changeIndex : 0);
    const durationData = DurationServiceInstance.getDurationData(duration);
    const { from, to, isWindow } = durationData.forAPI;
    const apiParams = {
        country,
        webSource,
        to,
        from,
        isWindow,
        keys: key,
        pageFilterJson: pageFilter,
        selectedPageTab: ELandingPagesTabs[ELandingPagesTabs.Organic],
        sourceType: ELandingPagesTabs[ELandingPagesTabs.Organic],

        ...(selectedChangeIndex > 0 && { Change: changeFilterItems[selectedChangeIndex]?.id }),
    };

    const visibleColumns = [TABLE_COLUMN_TOP_URL, TABLE_COLUMN_SHARE, TABLE_COLUMN_CLICKS_CHANGE, TABLE_COLUMN_CLICKS];

    const { data, isLoading, isError } = useQuery({
        queryKey: [
            API,
            to,
            from,
            duration,
            webSource,
            key,
            country,
            selectedPageTab,
            pageFilter,
            selectedChangeIndex,
            TopOrganicPagesFilter,
        ],
        queryFn: async () => DefaultFetchService.getInstance().get<ITopOrganicPagesTableData>(API, apiParams),
        select: (response: ITopOrganicPagesTableData) => transformedData(response),
    });

    const link = proNavigator.href("organicsearch_website_landingpages_v2", {
        key,
        mtd: false,
        webSource,
        selectedPageTab: ELandingPagesTabs[ELandingPagesTabs.Organic],
        country,
        pageFilter,
        duration,
        ...(selectedChangeIndex > 0 && { Change: changeFilterItems[selectedChangeIndex]?.id }),
    });

    const columns = getColumns(isWindow, visibleColumns) as ColumnsType<ITransformedData>;

    const footerCTA = {
        type: buttonTypes.FLAT,
        text: translate(WIDGET_FOOTER_PAGES_LINK_TEXT),
        link,
        onClick: () => {
            allTrackers.dispatchTrackEvent("Internal Link", "click", "Competitive Opportunities/Top Organic Pages");
        },
    };

    const handleChangeFilterSelection = (value: number) => {
        setSelectedChangeIndex(value);
        proNavigator.updateQueryParams({ topOrganicPagesFilter: changeFilterItems[value].id }, { reload: false });
    };

    return (
        <TableWrapper data-automation="top-organic-pages">
            <WidgetContainer
                title={translate(WIDGET_PAGES_TITLE_KEY)}
                tooltip={translate(WIDGET_PAGES_TOOLTIP_KEY)}
                isLoading={false}
                loaderWidth={"100%"}
                loaderHeight={132}
                noData={false}
                withBorder={false}
                noDataTitle={translate("common.noresults")}
                noDataSubtitle={translate("keyword.analysis.overview.page.v2.trend_over_time.noData.subtitle")}
                footerButtonsConfig={[footerCTA]}
            >
                <SwitcherWrapper
                    onItemClick={handleChangeFilterSelection}
                    selectedIndex={selectedChangeIndex}
                    customClass="CircleSwitcher"
                >
                    {changeFilterItems.map((item, index) => (
                        <CircleSwitcherItem selected={selectedChangeIndex === index}>
                            {translate(item.text)}
                        </CircleSwitcherItem>
                    ))}
                </SwitcherWrapper>
                <Table<(typeof data)[number]>
                    columnConfig={columns}
                    data={data ?? []}
                    isLoading={isLoading}
                    loaderRowCount={5}
                    noData={getNoDataObject(isError)}
                    sticky={false}
                    theme={"white"}
                    bordersConfig={{ header: false, body: false }}
                />
            </WidgetContainer>
        </TableWrapper>
    );
};
